import { type ConnectorParameterUndoPlan } from '../../types/parameterSetupUndo';

/**
 * Shown whenever a stored setup record cannot be executed. It says what the
 * user has to do next, because no automatic revert is possible any more.
 */
export const UnusableUndoPlanMessage =
  'The saved parameter setup record is incomplete, so it can no longer be reverted automatically. ' +
  'Remove the added parameters in Revit, then run the setup again.';

/** Only an intact `available` record may still be sent to Revit. */
export const isUndoPlanExecutable = (
  plan: ConnectorParameterUndoPlan | null | undefined,
): boolean => plan?.status === 'available';

export const isUndoPlanUnusable = (
  plan: ConnectorParameterUndoPlan | null | undefined,
): boolean => plan?.status === 'unusable';

/**
 * Whether the plan carries anything the undo actually sends to Revit. Counts
 * are a claim about the past; only the entries and project parameter names are
 * material an undo can act on.
 */
export const hasConnectorUndoWork = (
  plan: ConnectorParameterUndoPlan | null | undefined,
): boolean => {
  if (!plan) {
    return false;
  }

  // A record written by an older build can be missing a list entirely. What is
  // not there carries nothing to send, which is exactly what is asked here, so
  // the answer is "no work" rather than a crash on the step that shows it.
  return (
    (Array.isArray(plan.projectParameterNames) && plan.projectParameterNames.length > 0) ||
    (Array.isArray(plan.projectEntries) && plan.projectEntries.length > 0) ||
    (Array.isArray(plan.entries) && plan.entries.length > 0)
  );
};

/**
 * Keeps the record as recovery information and strips everything executable
 * from it. The claimed scale and the applied target stay, because together
 * they tell the user what is still in the model and how much of it there is.
 */
export const markUndoPlanUnusable = (
  plan: ConnectorParameterUndoPlan,
  error: string,
): ConnectorParameterUndoPlan => {
  const { familyParameterRestoreEntries, projectEntries, ...rest } = plan;

  return {
    ...rest,
    status: 'unusable',
    error,
    entries: [],
    projectParameterNames: [],
    projectEntries: [],
  };
};
